import { useState } from 'react';
import { Card, Title, Text, Box, Link, Icon } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { ErrorScreen, Responsive } from 'commons/components';
import { useModal } from 'commons/hooks';
import { useTranslationAccount } from 'domains/Account/hooks';
import { UseBusinessDataResult } from 'domains/Account/hooks/useBusinessData/types';
import { useGetArTaxRulesQuery } from 'domains/Invoices/invoicesApi';
import {
  GeneralTax,
  ExceptionsResultDesktop,
  TaxExceptionsModal,
  ExceptionsResultMobile,
  DeleteConfirmationModal,
} from './components';
import { TaxExceptionsProvider } from './ProductsTaxExceptions';
import Skeleton from './Skeleton';
import { GeneralInformationBusinessDataCardValues } from '../GeneralInformationBusinessDataCard/GeneralInformationBusinessDataCard';

interface TaxExceptionsCardProps {
  readonly values: GeneralInformationBusinessDataCardValues;
  readonly onChange: UseBusinessDataResult['setBusinessDataValues'];
}

function TaxExceptionsCard({ values, onChange }: TaxExceptionsCardProps) {
  const [isShowModal, openModal, closeModal] = useModal();
  const [showDeleteModal, openDeleteModal, closeDeleteModal] = useModal();
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const {
    data: taxRules = [],
    isLoading,
    isError,
    refetch,
  } = useGetArTaxRulesQuery();
  const exceptionsData = taxRules.filter((item) => !item.is_general);

  const t = useTranslationAccount('businessDataPage.taxesWithExceptions');

  const handleDelete = (index: number) => {
    if (isShowModal) {
      closeModal();
    }
    setEditingItem(index);
    openDeleteModal();
  };

  const handleAddNew = () => {
    setEditingItem(null);
    openModal();
  };

  const handleCloseModal = () => {
    setEditingItem(null);
    closeModal();
  };

  const handleCloseDeleteModal = () => {
    setEditingItem(null);
    closeDeleteModal();
  };

  const handleEdit = (index: number) => {
    setEditingItem(index);
    openModal();
  };

  const handleCheckChange = (checked: boolean) => {
    onChange({ name: 'orderTaxesEnabled', value: checked });
  };

  const handleChangeTaxRate = (taxRate: number) => {
    onChange({ name: 'orderTaxesPercent', value: taxRate });
  };

  if (isLoading) {
    return <Skeleton />;
  }

  if (isError) {
    return (
      <Card>
        <ErrorScreen
          description={t('businessDataPage.errorState.description')}
          onRetry={refetch}
        />
      </Card>
    );
  }
  const selectedItem =
    editingItem !== null ? exceptionsData[editingItem] : null;

  return (
    <Card>
      <Card.Header>
        <Title as="h4">{t('title')}</Title>
      </Card.Header>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <GeneralTax
            checked={values.orderTaxesEnabled}
            taxRate={values.orderTaxesPercent}
            onCheckChange={handleCheckChange}
            onChange={handleChangeTaxRate}
          />
          {values.orderTaxesEnabled && (
            <>
              <Box display="flex" flexDirection="column" gap="2">
                <Text fontWeight="bold">{t('subtitle')}</Text>
                <Text>{t('description')}</Text>
              </Box>
              <Link
                appearance="primary"
                as="button"
                textDecoration="none"
                onClick={handleAddNew}
              >
                <Icon color="currentColor" source={<PlusCircleIcon />} />
                {t('addExceptions')}
              </Link>
              {exceptionsData.length > 0 && (
                <Responsive
                  mobile={
                    <ExceptionsResultMobile
                      data={exceptionsData}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete}
                    />
                  }
                  desktop={
                    <ExceptionsResultDesktop
                      data={exceptionsData}
                      handleEdit={handleEdit}
                      handleDelete={handleDelete}
                    />
                  }
                />
              )}
              {isShowModal && (
                <TaxExceptionsProvider>
                  <TaxExceptionsModal
                    onClose={handleCloseModal}
                    isOpen={isShowModal}
                    editingItem={
                      editingItem !== null ? exceptionsData[editingItem] : null
                    }
                  />
                </TaxExceptionsProvider>
              )}
              {showDeleteModal && selectedItem && (
                <DeleteConfirmationModal
                  onClose={handleCloseDeleteModal}
                  itemDeleted={selectedItem}
                  showDeleteModal={showDeleteModal}
                />
              )}
            </>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}

export default TaxExceptionsCard;
